import type { ComputedRef, Ref } from 'vue';
import type { ControlError } from '@/shared/forms';

/** The Field props the state reads, as refs (`toRefs(props)`) */
export interface FieldStateProps {
    label: Ref<string | undefined>;
    description: Ref<string | undefined>;
    hint: Ref<string | undefined>;
    error: Ref<string | undefined>;
    errors: Ref<readonly ControlError[]>;
    invalid: Ref<boolean>;
    id: Ref<string | undefined>;
}

export interface FieldState {
    controlId: ComputedRef<string>;
    descriptionId: ComputedRef<string>;
    errorId: ComputedRef<string>;
    /** The one error message that shows */
    message: ComputedRef<string | undefined>;
    isInvalid: ComputedRef<boolean>;
    hasLabel: ComputedRef<boolean>;
    hasHint: ComputedRef<boolean>;
    showDescription: ComputedRef<boolean>;
    describedBy: ComputedRef<string | undefined>;
}

/** Ids, the error message and `aria-describedby` shared by Field and ChoiceField. */
export function useFieldState({ label, description, hint, error, errors, invalid, id }: FieldStateProps, slots: { label?: unknown; description?: unknown; hint?: unknown }): FieldState {
    const generatedId = useId();
    const controlId = computed(() => id.value ?? generatedId);
    const descriptionId = computed(() => `${controlId.value}-description`);
    const errorId = computed(() => `${controlId.value}-error`);

    /* Only the first message shows, so a field never grows by more than one line: `error` first, then the Control errors in order. */
    const message = computed(() => error.value || errors.value[0]?.message);
    const isInvalid = computed(() => invalid.value || !!message.value);

    const hasLabel = computed(() => !!(label.value || slots.label));
    const hasHint = computed(() => !!(hint.value || slots.hint));
    const showDescription = computed(() => !!(description.value || slots.description) && !message.value);

    const describedBy = computed(() => {
        const ids = [showDescription.value && descriptionId.value, message.value && errorId.value].filter(Boolean);
        return ids.length > 0 ? ids.join(' ') : undefined;
    });

    return { controlId, descriptionId, errorId, message, isInvalid, hasLabel, hasHint, showDescription, describedBy };
}
